import type {
  AllMembershipPlanType,
  ApiHookReturn,
  MarketedMembershipPlan,
  MarketedMembershipPlanType,
  MembershipPlanShape,
} from '@frontend/api-client';
import { getMembershipPlanDisplayPricing } from '@frontend/api-client';
import type { MutationHandler } from '@frontend/duck-tape';
import type { TypedSegmentTrack } from '@frontend/react';
import { createContext, useEffect, useMemo, useReducer } from '@frontend/react';

import type { PromotionTranslationActions, PromotionTranslationState } from '@frontend/react-domain/paywall';
import { usePromotionTranslation } from '@frontend/react-domain/paywall';
import type { PricingState, ValidatedPromoInput } from '../paywall/getValidatedPromoInput';
import { getValidatedPromoInput } from '../paywall/getValidatedPromoInput';
import { useDefaultPromoCode } from '../paywall/useDefaultPromoCode';
import type { MembershipWizardAction, MembershipWizardState } from './membershipStateMachine';
import { membershipInitialState, membershipStateMachine } from './membershipStateMachine';

type MembershipPlansResult = {
  displayPlanLookup: Partial<Record<AllMembershipPlanType, MembershipPlanShape>>;
  displaySelectablePlans: Array<MembershipPlanShape & { planType: MarketedMembershipPlanType }>;
  isError: boolean;
  isFetching: boolean;
  isLoading: boolean;
  pricingError?: 'planTiersFailed' | 'promoPricingFailed';
  pricingState: PricingState;
  refetch: () => void;
};

type MembershipWizardDeps = {
  handleMutation: MutationHandler;
  retrieveClientHookResult: ApiHookReturn<'useRetrieveClientQuery'>;
  retrievePromotionDetailsHookResult: ApiHookReturn<'useLazyRetrievePromotionDetailsQuery'>;
  track: TypedSegmentTrack;
  useMembershipPlans: (options?: { validatedPromo?: ValidatedPromoInput }) => MembershipPlansResult;
};

type MembershipWizardContextValue = {
  amountDueToday: MaybeUndefined<Cent>;
  dispatch: React.Dispatch<MembershipWizardAction>;
  membershipPlans: MembershipPlansResult;
  overridePromoCode?: string;
  promoActions: PromotionTranslationActions;
  promoState: PromotionTranslationState;
  selectedPlanData: MaybeUndefined<MarketedMembershipPlan>;
  state: MembershipWizardState;
  track: TypedSegmentTrack;
};

export const MembershipWizardContext = createContext<MembershipWizardContextValue | undefined>(undefined);

export type MembershipWizardProviderProps = {
  children: React.ReactNode;
  deps: MembershipWizardDeps;
  overridePromoCode?: string;
};

export const MembershipWizardProvider = ({ children, deps, overridePromoCode }: MembershipWizardProviderProps) => {
  const { handleMutation, retrieveClientHookResult, retrievePromotionDetailsHookResult, track, useMembershipPlans } =
    deps;
  const [state, dispatch] = useReducer(membershipStateMachine, membershipInitialState);

  const defaultPromoCode = useDefaultPromoCode({
    overridePromoCode,
    retrieveClientHookResult,
  });

  const { actions: promoActions, state: promoState } = usePromotionTranslation({
    defaultPromoCode,
    firstPurchaseData: {
      selectedPlanData: undefined,
      selectedPurchaseType: 'Subscription',
    },
    handleMutation,
    retrievePromotionDetailsHookResult,
  });

  const validatedPromo = useMemo(
    () =>
      getValidatedPromoInput({
        promoCode: promoState.promoCode,
        promotionId: promoState.promotionDetails?.promotionId,
      }),
    [promoState.promoCode, promoState.promotionDetails?.promotionId],
  );

  const membershipPlans = useMembershipPlans({ validatedPromo });

  const selectedPlanData: MaybeUndefined<MarketedMembershipPlan> = state.selectedPlanType
    ? (membershipPlans.displayPlanLookup[state.selectedPlanType] as MarketedMembershipPlan | undefined)
    : undefined;

  useEffect(() => {
    if (promoState.hasSuccessfullyAppliedPromoCode && promoState.promotionDetails && promoState.promoCode) {
      if (
        state.promoCode === promoState.promoCode &&
        state.promotionDetails?.promotionId === promoState.promotionDetails.promotionId
      ) {
        return;
      }

      dispatch({
        promoCode: promoState.promoCode,
        promotionDetails: promoState.promotionDetails,
        type: 'applyPromo',
      });

      return;
    }

    if (state.promoCode || state.promotionDetails) {
      dispatch({ type: 'dismissPromo' });
    }
  }, [
    promoState.hasSuccessfullyAppliedPromoCode,
    promoState.promoCode,
    promoState.promotionDetails,
    state.promoCode,
    state.promotionDetails,
  ]);

  useEffect(() => {
    if (
      state.step === 'paymentCapture' &&
      state.selectedPlanType &&
      membershipPlans.pricingState === 'ready' &&
      !selectedPlanData
    ) {
      dispatch({ type: 'back' });
    }
  }, [membershipPlans.pricingState, selectedPlanData, state.selectedPlanType, state.step]);

  const amountDueToday = useMemo(
    () => getMembershipPlanDisplayPricing(selectedPlanData)?.primaryPrice,
    [selectedPlanData],
  );

  const contextValue = useMemo(
    (): MembershipWizardContextValue => ({
      amountDueToday,
      dispatch,
      membershipPlans,
      overridePromoCode,
      promoActions,
      promoState,
      selectedPlanData,
      state,
      track,
    }),
    [
      amountDueToday,
      dispatch,
      membershipPlans,
      overridePromoCode,
      promoActions,
      promoState,
      selectedPlanData,
      state,
      track,
    ],
  );

  return <MembershipWizardContext.Provider value={contextValue}>{children}</MembershipWizardContext.Provider>;
};

export type { MembershipWizardContextValue, MembershipWizardDeps };
